/**
 * Shared language mapping utilities for code syntax highlighting
 * Used by diff viewers and tokenizers throughout the application
 */

// Map file extension to language
export const languageMap: Record<string, string> = {
  // JavaScript/TypeScript
  ts: 'typescript',
  tsx: 'tsx',
  js: 'javascript',
  jsx: 'jsx',
  mjs: 'javascript',
  cjs: 'javascript',

  // Web
  html: 'markup',
  css: 'css',
  scss: 'css',
  sass: 'css',
  less: 'css',
  vue: 'markup',

  // Backend
  py: 'python',
  rb: 'ruby',
  go: 'go',
  rs: 'rust',
  java: 'java',
  kt: 'kotlin',
  scala: 'scala',
  php: 'php',
  cs: 'csharp',
  cpp: 'cpp',
  c: 'clike',
  cc: 'clike',
  h: 'clike',
  hpp: 'clike',

  // Data/Config
  json: 'json',
  yml: 'yaml',
  yaml: 'yaml',
  toml: 'typescript',
  xml: 'xml',
  md: 'markdown',
  mdx: 'markdown',

  // Shell
  sh: 'bash',
  bash: 'bash',
  zsh: 'bash',
  fish: 'bash',

  // Other
  sql: 'sql',
  swift: 'swift',
  prisma: 'typescript', // Use typescript highlighting for Prisma
  dart: 'dart',
  ex: 'elixir',
  exs: 'elixir',
  elm: 'elm',
  graphql: 'graphql',
  gql: 'graphql',
  sol: 'solidity',
  zig: 'zig',
  groovy: 'groovy',
  gradle: 'groovy',
  nginx: 'nginx',
  conf: 'nginx',

  // Infrastructure as Code
  tf: 'hcl',
  tfvars: 'hcl',
  hcl: 'hcl',
  dockerfile: 'docker',
  makefile: 'makefile',
};

// List of languages registered with refractor
export const registeredLanguages = new Set([
  'jsx',
  'tsx',
  'javascript',
  'typescript',
  'markdown',
  'css',
  'json',
  'yaml',
  'bash',
  'python',
  'clike',
  'cpp',
  'java',
  'php',
  'ruby',
  'go',
  'rust',
  'csharp',
  'swift',
  'kotlin',
  'sql',
  'markup', // HTML
  'xml',
  'hcl', // Terraform/HashiCorp Configuration Language
  'dart',
  'elixir',
  'elm',
  'graphql',
  'solidity',
  'zig',
  'groovy',
  'nginx',
  'docker',
  'makefile',
]);

/**
 * Get language for syntax highlighting based on filename
 * @param filename The filename to determine language from
 * @param providedLanguage Optional explicit language to use instead of detecting
 * @returns The language identifier for syntax highlighting
 */
export function getLanguageFromFilename(filename: string, providedLanguage?: string): string {
  // If language is provided and registered, use it
  if (providedLanguage && registeredLanguages.has(providedLanguage)) {
    return providedLanguage;
  }

  // Check for specific filenames first
  if (filename.endsWith('schema.prisma') || filename.endsWith('.prisma')) {
    return 'typescript'; // Use typescript highlighting for Prisma
  }

  // Handle special filenames without extensions
  const basename = filename.split('/').pop()?.toLowerCase() || '';
  if (basename === 'dockerfile' || basename.startsWith('dockerfile.')) {
    return 'docker';
  }
  if (basename === 'makefile' || basename.startsWith('makefile.')) {
    return 'makefile';
  }

  // Extract extension
  const extension = filename.split('.').pop()?.toLowerCase() || '';

  // Look up in language map, default to text if not found
  return languageMap[extension] || 'text';
}
